import React, { useState, useEffect } from 'react';
import { useUI } from 'src/context/UIContext';
import BookShelf from 'src/components/books/BookShelf';
import BookGrid from 'src/components/books/BookGrid';
import BookList from 'src/components/books/BookList';
import AuthLoginModal from 'src/components/books/AuthLoginModal';
import BookSeriesModal from 'src/components/books/BookSeriesModal';
import ContactModal from 'src/components/ContactModal';
import { fetchBookSeries, fetchBookSeriesWithToken, getStoredToken, clearStoredToken } from 'src/lib/booksApi';

const PROFILE_BIO = [
    "I write from Victoria, Australia, thrillers and mysteries that lean towards noir, though I'd say I'm less interested in darkness than in the people who find themselves standing in it.",
    "My stories tend to move slowly towards something inevitable. The tension isn't in what happens, but in why. I'm constantly considering what quietly accumulates inside a person until a decision is made that can't be undone. I find myself drawn to that moment before the knock on the door or the drop of a hammer. What it costs someone to raise their hand. What they're hoping they won't find on the other side.",
    "I write honestly rather than hopefully. My characters don't always come out better for what they've been through, but they always come out true to themselves, and that, to me, matters more than a happy ending.",
    "Writing is how I decompress, how I think, and how I quietly love the craft of a well-told story. At home, I have a wife, three kids, and probably a plot problem I'm turning over in the back of my mind.",
    "I love talking about the craft and am always open to collaboration. If you're working through a character that isn't quite landing, or a plot that won't come together, I'd genuinely enjoy thinking it through with you. Feel free to reach out.",
];

function fmtUpdated(iso) {
    if (!iso) return '';
    const [y, m] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[Number(m) - 1]} ${y}`;
}

function SectionHeader({ title, sub }) {
    return (
        <div className="section-header">
            <h2>{title}</h2>
            {sub && <p className="section-sub">{sub}</p>}
        </div>
    );
}

export default function BooksPage() {
    const { bookLayout, setBookLayout, signedIn, setSignedIn } = useUI();
    const [bookSeries, setBookSeries]     = useState([]);
    const [isLoading, setIsLoading]       = useState(true);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [contactOpen, setContactOpen]   = useState(false);
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [seriesModalOpen, setSeriesModalOpen] = useState(false);

    // On mount: try stored token first, then auto-login as guest
    useEffect(() => {
        const token = getStoredToken();
        if (token) {
            fetchBookSeriesWithToken(token)
                .then((series) => {
                    setBookSeries(series);
                    setSignedIn(true);
                })
                .catch(() => {
                    clearStoredToken();
                    autoGuestLogin();
                })
                .finally(() => setIsLoading(false));
        } else {
            autoGuestLogin();
        }
    }, [setSignedIn]);

    function autoGuestLogin() {
        fetchBookSeries('guest')
            .then((series) => setBookSeries(series))
            .catch(() => {}) // silently fail — empty collection shown
            .finally(() => setIsLoading(false));
    }

    const handleLogin = async (authType, credentials = {}) => {
        try {
            const series = await fetchBookSeries(authType, credentials);
            setBookSeries(series);
            setSignedIn(authType === 'user');
            setLoginModalOpen(false);
        } catch (err) {
            throw err; // let AuthLoginModal handle the error display
        }
    };

    const handleSignOut = () => {
        clearStoredToken();
        setSignedIn(false);
        autoGuestLogin();
    };

    const handleSeriesClick = (series) => {
        setSelectedSeries(series);
        setSeriesModalOpen(true);
    };

    // Featured series: the one flagged featured:true from Lambda, or first series
    const featured = bookSeries.find(s => s.featured) || bookSeries[0] || null;

    const bookCollectionProps = {
        series: bookSeries,
        signedIn,
        onSignIn: () => setLoginModalOpen(true),
        onSeriesClick: handleSeriesClick,
    };

    return (
        <div>
            {/* ── Hero ── */}
            <section className="hero-author">
                <div className="author-hero-inner">
                    <div className="author-hero-grid">
                        {/* Left: bio */}
                        <div className="author-hero-bio">
                            <div className="eyebrow">Author · Victoria, Australia</div>
                            <h1 className="hero-name">Matthew Brinkmann</h1>
                            <p className="author-tagline">Thrillers &amp; mysteries that lean towards noir.</p>

                            <div className="bio-prose">
                                {PROFILE_BIO.map((para, i) =>
                                    i === PROFILE_BIO.length - 1 ? (
                                        <p key={i}>
                                            {para.replace('Feel free to reach out.', '')}
                                            Feel free to{' '}
                                            <button
                                                className="link-arrow"
                                                style={{ display: 'inline', fontSize: 'inherit' }}
                                                onClick={() => setContactOpen(true)}
                                            >
                                                reach out
                                            </button>
                                            .
                                        </p>
                                    ) : (
                                        <p key={i}>{para}</p>
                                    )
                                )}
                            </div>

                            <div className="hero-cta">
                                <a className="link-arrow" href="#books">Browse the collection →</a>
                                <button className="link-arrow ghost" onClick={() => setContactOpen(true)}>
                                    Reach out →
                                </button>
                            </div>
                        </div>

                        {/* Right: currently writing */}
                        <aside className="featured-card">
                            <div className="featured-label">Currently writing</div>
                            {featured ? (
                                <div
                                    className={'featured-book clickable accent-' + (featured.accent || 'sky')}
                                    onClick={() => handleSeriesClick(featured)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSeriesClick(featured); }}
                                >
                                    <div className="featured-spine-block">
                                        <span className="featured-spine-text">{featured.title}</span>
                                    </div>
                                    <div className="featured-body">
                                        <div className="featured-status">
                                            <span className="featured-status-dot" />
                                            {featured.status || featured.stage}
                                        </div>
                                        <h3 className="featured-title">{featured.title}</h3>
                                        <div className="featured-genre">
                                            {featured.genre} · {featured.count ?? featured.books?.length ?? 0}{' '}
                                            {(featured.count ?? featured.books?.length ?? 0) === 1 ? 'book' : 'books'}
                                        </div>
                                        <p className="featured-blurb">{featured.blurb || featured.description}</p>
                                        {featured.lastUpdated && (
                                            <div className="featured-meta">
                                                Last updated {fmtUpdated(featured.lastUpdated)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="featured-book" style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {isLoading
                                        ? <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</span>
                                        : <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No series yet</span>
                                    }
                                </div>
                            )}
                        </aside>
                    </div>
                </div>
            </section>

            {/* ── Book Collection ── */}
            <section id="books" className="section">
                <div className="section-inner">
                    <SectionHeader
                        title="Book Collection"
                        sub={signedIn
                            ? 'Full access — click any series to explore details and download.'
                            : 'Browse what\'s public. Sign in to unlock drafts and download manuscripts.'}
                    />

                    {/* Toolbar */}
                    <div className="books-toolbar">
                        <div className="layout-switch" role="tablist" aria-label="Book layout">
                            {['shelf', 'grid', 'list'].map(id => (
                                <button
                                    key={id}
                                    role="tab"
                                    aria-selected={bookLayout === id}
                                    className={'layout-btn' + (bookLayout === id ? ' is-active' : '')}
                                    onClick={() => setBookLayout(id)}
                                >
                                    {id.charAt(0).toUpperCase() + id.slice(1)}
                                </button>
                            ))}
                        </div>
                        <button
                            className={'auth-badge' + (signedIn ? ' is-in' : ' is-out')}
                            onClick={signedIn ? handleSignOut : () => setLoginModalOpen(true)}
                        >
                            <span className="auth-dot" />
                            {signedIn ? 'Signed in' : 'Guest'}
                            <span className="auth-action">{signedIn ? 'Sign out' : 'Sign in'}</span>
                        </button>
                    </div>

                    {/* Collection */}
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ink-3)', fontFamily: 'var(--font-display)', fontSize: 13 }}>
                            Loading collection…
                        </div>
                    ) : (
                        <>
                            {bookLayout === 'shelf' && <BookShelf {...bookCollectionProps} />}
                            {bookLayout === 'grid'  && <BookGrid  {...bookCollectionProps} />}
                            {bookLayout === 'list'  && <BookList  {...bookCollectionProps} />}
                        </>
                    )}
                </div>
            </section>

            {/* ── Modals ── */}
            <AuthLoginModal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                onLogin={handleLogin}
            />

            <BookSeriesModal
                isOpen={seriesModalOpen}
                onClose={() => { setSeriesModalOpen(false); setSelectedSeries(null); }}
                series={selectedSeries}
                isGuest={!signedIn}
                onSignIn={() => { setSeriesModalOpen(false); setSelectedSeries(null); setLoginModalOpen(true); }}
            />

            <ContactModal
                isOpen={contactOpen}
                onClose={() => setContactOpen(false)}
            />
        </div>
    );
}
